import { useLocalStorageValue } from "@react-hookz/web";
import { NumberParam, useQueryParams } from "use-query-params";
import { useAppSelector } from "@/redux/hooks";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";

const PaginationParams = {
  page: NumberParam,
  pageSize: NumberParam,
};

export const usePagination = (name = "pagination", defaultPageSize = 12) => {
  /**
   * The selector, not `useOperator()` — that throws when there is no active operator, and this hook is
   * called from public routes (guest booking, job rating, invoice payment) where there isn't one.
   */
  const activeOperator = useAppSelector(activeOperatorSelector);

  /**
   * Scoped per operator so page size doesn't follow you across a `switchOperator` — it used to key on
   * the bare name, so setting Bookings to 100 rows under one operator silently applied it under every
   * other. Matches how the booking table's other preferences are keyed.
   *
   * Falls back to the bare name off-operator, which is the only thing public routes can key on.
   */
  const storageKey = activeOperator ? `${activeOperator.id}:${name}` : name;

  const { value: persistedPageSize, set: setPersistedPageSize } = useLocalStorageValue(storageKey, {
    defaultValue: defaultPageSize,
  });

  const [{ pageSize, page }, setPagination] = useQueryParams(PaginationParams);

  const handlePageClick = (page: number) => {
    setPagination({
      page,
    });
  };

  const handleSizeChange = (size: number) => {
    setPagination({
      page: undefined,
      pageSize: size,
    });
    setPersistedPageSize(size);
  };

  const clearPagination = () => {
    setPagination({
      page: undefined,
    });
  };

  return {
    page: page?.toString() ?? "1",
    pageSize: pageSize?.toString() ?? persistedPageSize?.toString() ?? defaultPageSize?.toString(),
    handlePageClick,
    handleSizeChange,
    clearPagination,
  };
};
